import type { DataMutate } from "../types/types";
import type {
  AttributeSchema,
  CollectionValueRowSchema,
  PIMProduct,
  PIMProductPartial,
} from "../types/types.PIM";

type RowIdReplacement = {
  from: string;
  to: string;
};

function mergeCollectionRowValues(
  existing: CollectionValueRowSchema["values"] | undefined,
  incoming: CollectionValueRowSchema["values"]
): CollectionValueRowSchema["values"] {
  if (!incoming.length) return existing ?? [];
  const byAttributeId = new Map(incoming.map((v) => [v.attribute_id, v]));
  const merged = (existing ?? []).map(
    (value) => byAttributeId.get(value.attribute_id) ?? value
  );
  const seen = new Set(merged.map((v) => v.attribute_id));
  for (const value of incoming) {
    if (!seen.has(value.attribute_id)) {
      merged.push(value);
      seen.add(value.attribute_id);
    }
  }
  return merged;
}

function mergeCollectionValueRows(
  existing: CollectionValueRowSchema[] | undefined,
  incoming: CollectionValueRowSchema[],
  rowIdReplacement?: RowIdReplacement
): CollectionValueRowSchema[] {
  const byId = new Map(incoming.map((r) => [r.row_id, r]));
  const seen = new Set<string>();
  const merged: CollectionValueRowSchema[] = [];

  for (const row of existing ?? []) {
    const lookupRowId =
      rowIdReplacement && row.row_id === rowIdReplacement.from
        ? rowIdReplacement.to
        : row.row_id;
    const patchRow = byId.get(lookupRowId);
    if (patchRow) {
      merged.push({
        ...row,
        ...patchRow,
        values: mergeCollectionRowValues(row.values, patchRow.values ?? []),
      });
      seen.add(lookupRowId);
    } else {
      merged.push(row);
    }
  }
  for (const row of incoming) {
    if (!seen.has(row.row_id)) merged.push(row);
  }
  return merged;
}

function mergeGroupAttributes(
  existing: AttributeSchema[] | undefined,
  incoming: AttributeSchema[]
): AttributeSchema[] {
  if (!incoming.length) return existing ?? [];
  const byId = new Map(incoming.map((a) => [a.id, a]));
  return (existing ?? []).map((attr) => {
    const patch = byId.get(attr.id);
    if (!patch) return attr;
    return {
      ...patch,
      choices: patch.choices ?? attr.choices,
    };
  });
}

// Merges updated column definitions (e.g. is_not_applicable) into the existing
// columns array, preserving columns not present in the partial.
function mergeCollectionColumns(
  existing: AttributeSchema[] | undefined,
  incoming: AttributeSchema[]
): AttributeSchema[] {
  if (!incoming.length) return existing ?? [];
  const byId = new Map(incoming.map((c) => [c.id, c]));
  return (existing ?? []).map((col) => {
    const patch = byId.get(col.id);
    if (!patch) return col;
    return {
      ...patch,
      choices: patch.choices ?? col.choices,
    };
  });
}

/**
 * Deep-merge a PATCH response body into the current cached product.
 * Top-level scalar fields are replaced when present; collections and groups
 * are merged row-by-row so unrelated data is preserved.
 */
function mergePartialIntoProduct(
  current: PIMProduct,
  partial: PIMProductPartial,
  rowIdReplacement?: RowIdReplacement
): PIMProduct {
  const next: PIMProduct = {
    ...current,
    ...(partial.name !== undefined && { name: partial.name }),
    ...(partial.display_status !== undefined && {
      display_status: partial.display_status,
    }),
    ...(partial.product_number !== undefined && {
      product_number: partial.product_number,
    }),
    ...(partial.primary_staged_product_id !== undefined && {
      primary_staged_product_id: partial.primary_staged_product_id,
    }),
  };

  const patchCols = partial.product_schema?.collections;
  const patchGroups = partial.product_schema?.groups;

  if (!patchCols?.length && !patchGroups?.length) return next;

  let { product_schema } = next;

  if (patchCols?.length) {
    product_schema = {
      ...product_schema,
      collections: product_schema.collections.map((col) => {
        const patch = patchCols.find((c) => c.id === col.id);
        if (!patch) return col;
        return {
          ...col,
          ...(patch.value_rows?.length && {
            value_rows: mergeCollectionValueRows(
              col.value_rows,
              patch.value_rows,
              rowIdReplacement
            ),
          }),
          ...(patch.columns?.length && {
            columns: mergeCollectionColumns(col.columns, patch.columns),
          }),
        };
      }),
    };
  }

  if (patchGroups?.length) {
    product_schema = {
      ...product_schema,
      groups: product_schema.groups.map((grp) => {
        const patch = patchGroups.find((g) => g.id === grp.id);
        return patch?.attributes?.length
          ? {
              ...grp,
              attributes: mergeGroupAttributes(
                grp.attributes,
                patch.attributes
              ),
            }
          : grp;
      }),
    };
  }

  return { ...next, product_schema };
}

function hasSchemaSlice(partial: PIMProductPartial): boolean {
  const cols = partial.product_schema?.collections ?? [];
  const groups = partial.product_schema?.groups ?? [];
  return (
    groups.length > 0 ||
    cols.some((c) => c.value_rows?.length || c.columns?.length)
  );
}

/**
 * Merge a PATCH response into the SWR cache immediately (no network call),
 * then schedule a seamless background GET for eventual consistency.
 *
 * Step 1 — instant UI update: the PATCH response rows are merged into the
 *           cached product without triggering a network request.
 * Step 2 — background GET: fires after the current paint via setTimeout(0)
 *           so the UI is never blocked or shows a loading state.
 *
 * Use this after Add / Edit operations that return a PIMProductPartial.
 */
export async function applyPartialPimProductAndRefresh(
  mutate: DataMutate<PIMProduct>,
  partial: PIMProductPartial,
  options?: {
    rowIdReplacement?: RowIdReplacement;
  }
): Promise<void> {
  await mutate(
    (prev) =>
      prev && hasSchemaSlice(partial)
        ? mergePartialIntoProduct(prev, partial, options?.rowIdReplacement)
        : prev,
    false
  );
  window.setTimeout(() => {
    void mutate();
  }, 0);
}

/**
 * Schedule a seamless background GET without blocking the UI.
 *
 * Use this after DELETE where there is no PATCH response body to merge.
 */
export function revalidatePimProductDeferred(
  mutate: DataMutate<PIMProduct>
): void {
  window.setTimeout(() => {
    void mutate();
  }, 0);
}
